import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DOMPurify from "dompurify";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

const ExamPage = () => {
  const [examData, setExamData] = useState(null);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentPassageIndex, setCurrentPassageIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null);
  const [questionStatuses, setQuestionStatuses] = useState({});
  const [userAnswers, setUserAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("questions");
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [fullscreenWarning, setFullscreenWarning] = useState(false);
  const [testData, setTestData] = useState(null);
  const [instructions, setInstructions] = useState("");

  const examId = localStorage.getItem("test_id");
  const testType = localStorage.getItem("test_type") || "demo_test";
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const username = localStorage.getItem("username");

  const timerRef = useRef(null);
  const blockCopyPasteRef = useRef(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible); // Toggle sidebar visibility
  };

  const modelId = localStorage.getItem("test_id");
  useEffect(() => {
    fetchInstructions();
  }, []);

  const fetchInstructions = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/model-paper-instructions`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json", // ✅ important!
          },
          body: JSON.stringify({
            id: modelId,
          }),
        }
      );

      const data = await res.json(); // ✅ parse JSON

      console.log("instructions", data);

      if (data.status === "1") {
        setInstructions(data.data.description || "");
        setTestData(data.data); // make sure testData is also set
      } else {
        setInstructions("No instructions available.");
      }
    } catch (error) {
      console.error("Error fetching instructions:", error);
      setInstructions("Error loading instructions.");
    }
  };
  // Security: Block right click, copy, paste, etc.
  useEffect(() => {
    const blockRightClick = (e) => {
      e.preventDefault();
      return false;
    };

    const blockCopyPaste = (e) => {
      if (e.type === "copy" || e.type === "cut" || e.type === "paste") {
        e.preventDefault();
        Swal.fire({
          icon: "warning",
          title: "Action Blocked",
          text: "Copying and pasting is not allowed during the exam.",
          timer: 2000,
          showConfirmButton: false,
        });
        return false;
      }
    };

    const blockKeyboardShortcuts = (e) => {
      if (
        e.key === "F12" ||
        (e.ctrlKey && e.shiftKey && e.key === "I") ||
        (e.ctrlKey && e.shiftKey && e.key === "C") ||
        (e.ctrlKey && e.key === "u") ||
        (e.ctrlKey && e.key === "c") ||
        (e.ctrlKey && e.key === "v") ||
        (e.ctrlKey && e.key === "x")
      ) {
        e.preventDefault();
        return false;
      }
    };

    const blockTextSelection = (e) => {
      if (e.type === "selectstart") {
        e.preventDefault();
        return false;
      }
    };

    // Add event listeners
    document.addEventListener("contextmenu", blockRightClick);
    document.addEventListener("copy", blockCopyPaste);
    document.addEventListener("cut", blockCopyPaste);
    document.addEventListener("paste", blockCopyPaste);
    document.addEventListener("keydown", blockKeyboardShortcuts);
    document.addEventListener("selectstart", blockTextSelection);

    // Check fullscreen mode
    const checkFullscreen = () => {
      if (!document.fullscreenElement) {
        setFullscreenWarning(true);
        Swal.fire({
          icon: "warning",
          title: "Fullscreen Required",
          text: "Please enable fullscreen mode to continue the exam.",
          confirmButtonText: "Enable Fullscreen",
          allowOutsideClick: false,
          allowEscapeKey: false,
        }).then(() => {
          document.documentElement.requestFullscreen().catch((err) => {
            console.log("Fullscreen error:", err);
          });
        });
      }
    };

    // Request fullscreen on component mount
    document.documentElement.requestFullscreen().catch((err) => {
      console.log("Fullscreen error:", err);
    });

    // Check fullscreen periodically
    const fullscreenCheckInterval = setInterval(checkFullscreen, 5000);

    // Cleanup function
    return () => {
      document.removeEventListener("contextmenu", blockRightClick);
      document.removeEventListener("copy", blockCopyPaste);
      document.removeEventListener("cut", blockCopyPaste);
      document.removeEventListener("paste", blockCopyPaste);
      document.removeEventListener("keydown", blockKeyboardShortcuts);
      document.removeEventListener("selectstart", blockTextSelection);
      clearInterval(fullscreenCheckInterval);

      // Exit fullscreen when component unmounts
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    };
  }, []);

  // Load saved state from localStorage
  useEffect(() => {
    const savedState = localStorage.getItem(`exam_state_${examId}`);
    if (savedState) {
      const state = JSON.parse(savedState);

      if (state.startTime && state.timeLeft) {
        const timeElapsed = Math.floor((Date.now() - state.startTime) / 1000);
        const actualTimeLeft = Math.max(0, state.timeLeft - timeElapsed);
        setTimeLeft(actualTimeLeft);
      } else {
        setTimeLeft(state.timeLeft);
      }

      setUserAnswers(state.userAnswers || {});
      setQuestionStatuses(state.questionStatuses || {});
      setCurrentSectionIndex(state.currentSectionIndex || 0);
      setCurrentPassageIndex(state.currentPassageIndex || 0);
      setCurrentQuestionIndex(state.currentQuestionIndex || 0);
    }
  }, [examId]);

  // Save state to localStorage
  useEffect(() => {
    if (examId && examData && timeLeft !== null) {
      const stateToSave = {
        timeLeft,
        userAnswers,
        questionStatuses,
        currentSectionIndex,
        currentPassageIndex,
        currentQuestionIndex,
        examTitle: examData.title,
        startTime: Date.now(),
      };
      localStorage.setItem(`exam_state_${examId}`, JSON.stringify(stateToSave));
    }
  }, [
    timeLeft,
    userAnswers,
    questionStatuses,
    currentSectionIndex,
    currentPassageIndex,
    currentQuestionIndex,
    examId,
    examData,
  ]);

  useEffect(() => {
    fetchExamData();
  }, []);

  // Initialize timer when examData is loaded
  useEffect(() => {
    if (examData && timeLeft === null) {
      const savedState = localStorage.getItem(`exam_state_${examId}`);
      if (!savedState) {
        setTimeLeft(examData.duration_minutes * 60);
      }
    }
  }, [examData, timeLeft, examId]);

  // Timer effect
  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0) {
      if (timeLeft === 0) {
        handleAutoSubmit();
      }
      return;
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [timeLeft]);

  const handleAutoSubmit = async () => {
    Swal.fire({
      title: "Time Up!",
      text: "Your test has been automatically submitted.",
      icon: "warning",
      confirmButtonText: "OK",
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then(() => {
      handleSubmitTest();
    });
  };

  const fetchExamData = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/model-qusition-list`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ id: examId }),
        }
      );
      const data = await res.json();

      if (data.status === "1" && data.data) {
        const exam = data.data;
        setExamData(exam);

        // Initialize question statuses and answers if not already loaded from localStorage
        if (Object.keys(userAnswers).length === 0) {
          initializeQuestionStates(exam);
        }
      } else {
        setError("Exam data not available");
      }
    } catch (err) {
      console.error("Error fetching exam data:", err);
      setError("Failed to load exam. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const initializeQuestionStates = (exam) => {
    const allQuestions = getAllQuestions(exam);
    const initialStatuses = {};
    const initialAnswers = {};

    allQuestions.forEach((q, index) => {
      initialStatuses[index] = "not_answered";
      initialAnswers[index] = {
        type: q.question_type,
        answer: null, // Initialize all answers as null
      };
    });

    setQuestionStatuses(initialStatuses);
    setUserAnswers(initialAnswers);
  };

  // Get all questions flattened with proper indexing
  const getAllQuestions = (data = examData) => {
    if (!data?.sections) return [];

    const allQuestions = [];
    data.sections.forEach((section, sectionIndex) => {
      if (section.passages) {
        section.passages.forEach((passage, passageIndex) => {
          if (passage.questions) {
            passage.questions.forEach((question, questionIndex) => {
              const globalIndex = allQuestions.length;
              allQuestions.push({
                ...question,
                sectionIndex,
                passageIndex,
                questionIndex,
                sectionId: section.id,
                passageId: passage.id,
                globalIndex,
                sectionTitle: section.section_title,
                passageTitle: passage.passage_title,
              });
            });
          }
        });
      }
    });
    return allQuestions;
  };

  // Get current question object
  const getCurrentQuestion = () => {
    const allQuestions = getAllQuestions();
    return (
      allQuestions.find(
        (q) =>
          q.sectionIndex === currentSectionIndex &&
          q.passageIndex === currentPassageIndex &&
          q.questionIndex === currentQuestionIndex
      ) || null
    );
  };

  // Get questions for current section and passage
  const getCurrentQuestions = () => {
    if (
      !examData?.sections?.[currentSectionIndex]?.passages?.[
        currentPassageIndex
      ]?.questions
    ) {
      return [];
    }
    return examData.sections[currentSectionIndex].passages[currentPassageIndex]
      .questions;
  };

  // Get current global index
  const getCurrentGlobalIndex = () => {
    const allQuestions = getAllQuestions();
    const currentQuestion = getCurrentQuestion();
    return currentQuestion ? currentQuestion.globalIndex : 0;
  };

  const getTotalQuestions = () => {
    return getAllQuestions().length;
  };

  // Get questions for current section only (for sidebar)
  const getCurrentSectionQuestions = () => {
    if (!examData?.sections?.[currentSectionIndex]) return [];

    const sectionQuestions = [];
    examData.sections[currentSectionIndex].passages.forEach(
      (passage, pIndex) => {
        if (passage.questions) {
          passage.questions.forEach((question, qIndex) => {
            sectionQuestions.push({
              ...question,
              sectionIndex: currentSectionIndex,
              passageIndex: pIndex,
              questionIndex: qIndex,
              localIndex: sectionQuestions.length, // Local index within section
            });
          });
        }
      }
    );
    return sectionQuestions;
  };

  const handleSubmitConfirmation = () => {
    const answeredQuestions = Object.values(userAnswers).filter(
      (answer) => answer.answer !== null && answer.answer !== ""
    ).length;
    const totalQuestions = getTotalQuestions();

    setShowSubmitModal(true);
  };

  // ==================== CORRECTED QUIZ SUBMISSION FUNCTION ====================
  const handleSubmitTest = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      // Calculate statistics
      const totalQuestions = getTotalQuestions();
      const answeredQuestions = Object.values(userAnswers).filter(
        (answer) => answer.answer !== null && answer.answer !== ""
      ).length;

      const markedQuestions = Object.values(questionStatuses).filter(
        (status) => status === "marked"
      ).length;

      const notAnsweredQuestions = totalQuestions - answeredQuestions;
      const testDuration = examData.duration_minutes * 60 - timeLeft;

      // ✅ CORRECTED: Prepare attempts array for ALL questions (answered + unanswered)
      const attempts = [];
      const allQuestions = getAllQuestions();

      allQuestions.forEach((question, globalIndex) => {
        const userAnswer = userAnswers[globalIndex]?.answer;

        // ✅ Send ALL questions, even unanswered ones with null/empty user_answer
        attempts.push({
          question_id: question.id,
          user_answer:
            userAnswer !== null && userAnswer !== "" ? userAnswer : null,
          // For unanswered questions, user_answer will be null
          quiz_minutes: Math.floor(testDuration / 60), // Convert to minutes
          section_id: question.sectionId,
          passage_id: question.passageId,
          question_type: question.question_type,
        });
      });

      // ✅ CORRECTED: Count actual attempts (non-null answers)
      const actualAnsweredCount = attempts.filter(
        (attempt) => attempt.user_answer !== null && attempt.user_answer !== ""
      ).length;

      console.log("📊 Submission Stats:", {
        totalQuestions,
        allQuestionsSent: attempts.length,
        answeredQuestions: actualAnsweredCount,
        unansweredQuestions: attempts.length - actualAnsweredCount,
      });

      console.log("📤 Sending ALL questions to backend:", attempts);

      // Prepare submission data for backend API
      const submissionData = {
        quiz_id: parseInt(examId),
        attempts: attempts,
        total_questions: totalQuestions,
        answered: actualAnsweredCount, // Use actual count from attempts
        not_answered: totalQuestions - actualAnsweredCount,
        marked: markedQuestions,
        test_duration: testDuration,
        package_id: examData.package_id || 0,
        package_plan_id: examData.package_plan_id || 0,
      };

      // Send data to backend API
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/quiz-attempts`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(submissionData),
        }
      );

      const result = await response.json();

      if (result.status === "1") {
        // Success - Save result data for display
        const resultData = {
          examData,
          userAnswers,
          questionStatuses,
          totalQuestions,
          answeredQuestions: actualAnsweredCount,
          markedQuestions,
          notAnsweredQuestions: totalQuestions - actualAnsweredCount,
          timeTaken: testDuration,
          allQuestions: getAllQuestions(),
          negativeMarking: examData.negative_marking || 0,
          backendResult: result.data, // Include backend calculated results
        };

        // Save result data to localStorage for ResultPage
        localStorage.setItem(
          `exam_result_${examId}`,
          JSON.stringify(resultData)
        );

        // Mark test as completed
        const user_id = localStorage.getItem("user_id");
        const attemptKey = `test_attempt_${examId}_${user_id}`;
        localStorage.setItem(attemptKey, "completed");

        // Clear exam state
        localStorage.removeItem(`exam_state_${examId}`);
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }

        setShowSubmitModal(false);

        // Show success message
        Swal.fire({
          title: "Success!",
          text: "Your test has been submitted successfully.",
          icon: "success",
          confirmButtonText: "View Results",
        }).then(() => {
          // Navigate to result page
          localStorage.removeItem(`exam_state_${examId}`);
          // 2. Clear any timer intervals
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          // 3. Clear any temporary exam data
          const examKeys = Object.keys(localStorage).filter(
            (key) => key.startsWith(`exam_`) || key.startsWith(`test_attempt_`)
          );
          examKeys.forEach((key) => localStorage.removeItem(key));
          navigate("/test-result", {
            state: {
              resultData,
              submissionId: result.data.user_test_id,
            },
            replace: true,
          });
        });
      } else {
        // Backend error
        throw new Error(result.message || "Failed to submit quiz");
      }
    } catch (error) {
      console.error("Submission error:", error);

      Swal.fire({
        title: "Submission Failed",
        text:
          error.message ||
          "There was an error submitting your test. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });

      // Fallback: Save locally if backend fails
      const totalQuestions = getTotalQuestions();
      const answeredQuestions = Object.values(userAnswers).filter(
        (answer) => answer.answer !== null && answer.answer !== ""
      ).length;

      const markedQuestions = Object.values(questionStatuses).filter(
        (status) => status === "marked"
      ).length;

      const notAnsweredQuestions = totalQuestions - answeredQuestions;
      const testDuration = examData.duration_minutes * 60 - timeLeft;

      const resultData = {
        examData,
        userAnswers,
        questionStatuses,
        totalQuestions,
        answeredQuestions,
        markedQuestions,
        notAnsweredQuestions,
        timeTaken: testDuration,
        allQuestions: getAllQuestions(),
        negativeMarking: examData.negative_marking || 0,
        backendError: true,
      };

      localStorage.setItem(`exam_result_${examId}`, JSON.stringify(resultData));
      localStorage.removeItem(`exam_state_${examId}`);

      navigate("/test-result", {
        state: { resultData },
        replace: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    if (seconds === null) return "00:00:00";
    const hrs = String(Math.floor(seconds / 3600)).padStart(2, "0");
    const mins = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
    const secs = String(seconds % 60).padStart(2, "0");
    return `${hrs}:${mins}:${secs}`;
  };

  const getNegativeMarking = (question) => {
    if (
      question.negative_marking !== null &&
      question.negative_marking !== undefined
    ) {
      return parseFloat(question.negative_marking);
    }
    if (examData.negative_marking) {
      return parseFloat(examData.negative_marking);
    }
    return 0;
  };

  const handleOptionChange = (optionLetter, optionText) => {
    const currentGlobalIndex = getCurrentGlobalIndex();

    const updatedAnswers = {
      ...userAnswers,
      [currentGlobalIndex]: {
        type: getCurrentQuestion().question_type,
        answer: optionLetter, // ✅ Store only A, B, C, D
        answerText: optionText, // ✅ Optional: Store text separately if needed
      },
    };
    setUserAnswers(updatedAnswers);

    setQuestionStatuses((prev) => ({
      ...prev,
      [currentGlobalIndex]: "answered",
    }));
  };

  const handleTextAnswerChange = (e) => {
    const answer = e.target.value;
    const currentGlobalIndex = getCurrentGlobalIndex();

    const updatedAnswers = {
      ...userAnswers,
      [currentGlobalIndex]: {
        type: getCurrentQuestion().question_type,
        answer: answer, // For text answers, store the actual text
      },
    };
    setUserAnswers(updatedAnswers);

    setQuestionStatuses((prev) => ({
      ...prev,
      [currentGlobalIndex]: answer.trim() !== "" ? "answered" : "not_answered",
    }));
  };

  const handleMarkAndSave = () => {
    const currentGlobalIndex = getCurrentGlobalIndex();

    setQuestionStatuses((prev) => ({
      ...prev,
      [currentGlobalIndex]: "marked",
    }));

    handleNextQuestion();
  };

  const handleNextQuestion = () => {
    const currentQuestions = getCurrentQuestions();

    // Try to move to next question in current passage
    if (currentQuestionIndex < currentQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      return;
    }

    // Try to move to next passage in current section
    const currentSection = examData.sections[currentSectionIndex];
    if (currentPassageIndex < currentSection.passages.length - 1) {
      setCurrentPassageIndex(currentPassageIndex + 1);
      setCurrentQuestionIndex(0);
      return;
    }

    // Try to move to next section
    if (currentSectionIndex < examData.sections.length - 1) {
      setCurrentSectionIndex(currentSectionIndex + 1);
      setCurrentPassageIndex(0);
      setCurrentQuestionIndex(0);
    }
  };

  const handlePreviousQuestion = () => {
    // Try to move to previous question in current passage
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      return;
    }

    // Try to move to previous passage in current section
    if (currentPassageIndex > 0) {
      const prevPassageIndex = currentPassageIndex - 1;
      const prevPassageQuestions =
        examData.sections[currentSectionIndex].passages[prevPassageIndex]
          .questions;
      setCurrentPassageIndex(prevPassageIndex);
      setCurrentQuestionIndex(prevPassageQuestions.length - 1);
      return;
    }

    // Try to move to previous section
    if (currentSectionIndex > 0) {
      const prevSectionIndex = currentSectionIndex - 1;
      const prevSection = examData.sections[prevSectionIndex];
      const lastPassageIndex = prevSection.passages.length - 1;
      const lastPassageQuestions =
        prevSection.passages[lastPassageIndex].questions;
      setCurrentSectionIndex(prevSectionIndex);
      setCurrentPassageIndex(lastPassageIndex);
      setCurrentQuestionIndex(lastPassageQuestions.length - 1);
    }
  };

  const handleQuestionNavigation = (
    sectionIndex,
    passageIndex,
    questionIndex
  ) => {
    setCurrentSectionIndex(sectionIndex);
    setCurrentPassageIndex(passageIndex);
    setCurrentQuestionIndex(questionIndex);
  };

  const handleSectionChange = (sectionIndex) => {
    setCurrentSectionIndex(sectionIndex);
    setCurrentPassageIndex(0);
    setCurrentQuestionIndex(0);
  };

  const handlePassageChange = (sectionIndex, passageIndex) => {
    setCurrentSectionIndex(sectionIndex);
    setCurrentPassageIndex(passageIndex);
    setCurrentQuestionIndex(0);
  };

  const getCurrentAnswer = () => {
    const currentGlobalIndex = getCurrentGlobalIndex();
    return userAnswers[currentGlobalIndex]?.answer || "";
  };

  const clearCurrentResponse = () => {
    const currentGlobalIndex = getCurrentGlobalIndex();
    const updatedAnswers = { ...userAnswers };
    updatedAnswers[currentGlobalIndex] = {
      type: getCurrentQuestion().question_type,
      answer: null, // Set to null when cleared
    };
    setUserAnswers(updatedAnswers);
    setQuestionStatuses((prev) => ({
      ...prev,
      [currentGlobalIndex]: "not_answered",
    }));
  };

  // Helper function to get question status for display
  const getQuestionStatus = (sectionIndex, passageIndex, questionIndex) => {
    const allQuestions = getAllQuestions();
    const question = allQuestions.find(
      (q) =>
        q.sectionIndex === sectionIndex &&
        q.passageIndex === passageIndex &&
        q.questionIndex === questionIndex
    );

    if (!question) return "not_answered";
    return questionStatuses[question.globalIndex] || "not_answered";
  };

  // Get status counts for current section only
  const getCurrentSectionStatusCounts = () => {
    const currentSectionQuestions = getCurrentSectionQuestions();
    let answered = 0,
      marked = 0,
      notAnswered = 0;

    currentSectionQuestions.forEach((question) => {
      const status = getQuestionStatus(
        currentSectionIndex,
        question.passageIndex,
        question.questionIndex
      );
      if (status === "answered") answered++;
      else if (status === "marked") marked++;
      else notAnswered++;
    });

    return { answered, marked, notAnswered };
  };

  // HTML sanitization functions
  const getSanitizedPassageContent = () => {
    if (
      !examData?.sections?.[currentSectionIndex]?.passages?.[
        currentPassageIndex
      ]?.passage_content
    ) {
      return null;
    }

    const rawContent =
      examData.sections[currentSectionIndex].passages[currentPassageIndex]
        .passage_content;
    return DOMPurify.sanitize(rawContent);
  };

  const getSanitizedQuestionText = (question) => {
    if (!question?.question_text) return "";
    return DOMPurify.sanitize(question.question_text);
  };

  const getSanitizedOptionText = (option) => {
    if (!option) return "";
    const optionValue =
      typeof option === "string" ? option : option.option_text;
    return DOMPurify.sanitize(optionValue);
  };

  if (loading) {
    return (
      <div className="exam-result-page">
        <div className="page_header"></div>
        <div className="result-loading">
          <div className="loading-spinner"></div>
          <h3>Loading exam...</h3>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="exam-error">{error}</div>;
  }

  if (!examData) {
    return <div className="exam-error">No exam data found</div>;
  }

  const currentQuestion = getCurrentQuestion();
  const currentQuestions = getCurrentQuestions();
  const currentAnswer = getCurrentAnswer();
  const isTimeUp = timeLeft === 0;
  const currentSectionQuestions = getCurrentSectionQuestions();
  const currentSectionQuestionsCount = currentSectionQuestions.length;
  const sectionStatusCounts = getCurrentSectionStatusCounts();
  const totalQuestions = getTotalQuestions();
  const answeredQuestions = Object.values(userAnswers).filter(
    (answer) => answer.answer !== null && answer.answer !== ""
  ).length;

  return (
    <div className="exam-page" ref={blockCopyPasteRef}>
      <div className="exam-header">
        <h5>
          {examData.title} - {examData.package_name || "Test"}
        </h5>
        <div
          className={`timer ${
            timeLeft !== null && timeLeft < 300 ? "warning" : ""
          }`}
        >
          Time Left: {formatTime(timeLeft)}
        </div>
      </div>

      <div className="content">
        <div className="question-area">
          {/* Section Navigation - Horizontal Tabs */}
          <div className="section-navigation">
            {examData.sections.map((section, sIndex) => (
              <div key={section.id} className="section-tab-container">
                <button
                  className={`section-tab ${
                    sIndex === currentSectionIndex ? "active" : ""
                  }`}
                  onClick={() => handleSectionChange(sIndex)}
                >
                  {section.section_title}
                </button>
                {/* Passage Dropdown for each section */}
                {section.passages.length > 1 && (
                  <div className="passage-dropdown">
                    {section.passages.map((passage, pIndex) => (
                      <button
                        key={passage.id}
                        className={`passage-option ${
                          sIndex === currentSectionIndex &&
                          pIndex === currentPassageIndex
                            ? "active"
                            : ""
                        }`}
                        onClick={() => handlePassageChange(sIndex, pIndex)}
                      >
                        {passage.passage_title || `Passage ${pIndex + 1}`}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Content Tabs */}
          <div className="content-tabs">
            <button
              className={`tab-btn ${activeTab === "questions" ? "active" : ""}`}
              onClick={() => setActiveTab("questions")}
            >
              Questions
            </button>
            <button
              className={`tab-btn ${
                activeTab === "instructions" ? "active" : ""
              }`}
              onClick={() => setActiveTab("instructions")}
            >
              Instructions
            </button>
          </div>

          {activeTab === "instructions" ? (
            <div className="instructions-tab">
              <h6>Exam Instructions</h6>
              <div
                className="instructions-content p-3 border rounded bg-light"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    instructions || "No instructions provided."
                  ),
                }}
              />
            </div>
          ) : (
            <>
              {/* Current Context */}
              <div className="current-context">
                <h6>
                  Section:{" "}
                  {examData.sections[currentSectionIndex]?.section_title}
                  {examData.sections[currentSectionIndex]?.passages[
                    currentPassageIndex
                  ]?.passage_title &&
                    ` • Passage: ${examData.sections[currentSectionIndex].passages[currentPassageIndex].passage_title}`}
                </h6>
                <span>
                  Question {currentQuestionIndex + 1} of{" "}
                  {currentQuestions.length}
                  (Section:{" "}
                  {currentSectionQuestions.findIndex(
                    (q) =>
                      q.passageIndex === currentPassageIndex &&
                      q.questionIndex === currentQuestionIndex
                  ) + 1}{" "}
                  of {currentSectionQuestionsCount})
                </span>
              </div>

              <div className="my_question">
                {examData.sections[currentSectionIndex]?.passages[
                  currentPassageIndex
                ]?.passage_content && (
                  <div className="passage-content">
                    <div className="passage-card">
                      <h6>Passage:</h6>
                      <div
                        className="passage-text"
                        dangerouslySetInnerHTML={{
                          __html: getSanitizedPassageContent(),
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Question Card with HTML support */}
                {currentQuestion && (
                  <div className="question-card">
                    <div
                      className="question-text"
                      dangerouslySetInnerHTML={{
                        __html: getSanitizedQuestionText(currentQuestion),
                      }}
                    />

                    {currentQuestion.question_type === "mcq" && (
                      <div className="options">
                        {currentQuestion.options &&
                          currentQuestion.options.map((option, idx) => {
                            const optionText =
                              typeof option === "string"
                                ? option
                                : option.option_text;
                            const optionKey =
                              typeof option === "string"
                                ? option
                                : option.id || idx;
                            const optionLetter = String.fromCharCode(65 + idx); // A, B, C, D

                            return (
                              <label key={optionKey} className="option">
                                <input
                                  type="radio"
                                  name="answer"
                                  checked={currentAnswer === optionLetter}
                                  onChange={() =>
                                    handleOptionChange(optionLetter, optionText)
                                  }
                                  disabled={isTimeUp}
                                />
                                <span className="option-letter">
                                  {/* {optionLetter}. */}
                                </span>
                                <span
                                  className="option-text"
                                  dangerouslySetInnerHTML={{
                                    __html: getSanitizedOptionText(option),
                                  }}
                                />
                              </label>
                            );
                          })}
                      </div>
                    )}

                    {currentQuestion.question_type === "true_false" && (
                      <div className="options">
                        <label className="option">
                          <input
                            type="radio"
                            name="answer"
                            checked={currentAnswer === "true"}
                            onChange={() => handleOptionChange("true")}
                            disabled={isTimeUp}
                          />
                          <span className="option-text">True</span>
                        </label>
                        <label className="option">
                          <input
                            type="radio"
                            name="answer"
                            checked={currentAnswer === "false"}
                            onChange={() => handleOptionChange("false")}
                            disabled={isTimeUp}
                          />
                          <span className="option-text">False</span>
                        </label>
                      </div>
                    )}

                    {currentQuestion.question_type === "short_answer" && (
                      <div className="text-answer">
                        <textarea
                          value={currentAnswer}
                          onChange={handleTextAnswerChange}
                          placeholder="Type your answer here..."
                          rows={5}
                          disabled={isTimeUp}
                          onCopy={(e) => e.preventDefault()}
                          onPaste={(e) => e.preventDefault()}
                          onCut={(e) => e.preventDefault()}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Question Actions */}
              <div className="question-actions">
                <div className="action-buttons-left">
                  <button
                    className="btn mark-save"
                    onClick={handleMarkAndSave}
                    disabled={isTimeUp}
                  >
                    Mark & Save
                  </button>
                  <button
                    className="btn clear"
                    onClick={clearCurrentResponse}
                    disabled={isTimeUp}
                  >
                    Clear Response
                  </button>
                </div>
                <div className="navigation-buttons">
                  <button
                    className="btn previous"
                    onClick={handlePreviousQuestion}
                    disabled={isTimeUp}
                  >
                    Previous
                  </button>
                  <button
                    className="btn save-next"
                    onClick={handleNextQuestion}
                    disabled={isTimeUp}
                  >
                    Save & Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Sidebar */}
        <div
          className={`only_mobile_btn ${isSidebarVisible ? "hide" : ""}`}
          onClick={toggleSidebar}
        >
          {isSidebarVisible ? <FaChevronRight /> : <FaChevronLeft />}
        </div>
        <div className={`sidebar ${isSidebarVisible ? "active" : ""}`}>
          <div className="profile-info">
            <p>{username || "Student"}</p>
          </div>

          <div className="right_box">
            {/* Current Section Info */}
            <div className="current-section-info">
              <h6>{examData.sections[currentSectionIndex]?.section_title}</h6>
              <p>Questions: {currentSectionQuestionsCount}</p>
            </div>

            <div className="status-box">
              <p>
                <div className="status green">
                  <span>{sectionStatusCounts.answered}</span>
                </div>
                Answered
              </p>
              <p>
                <div className="status yellow">
                  <span>{sectionStatusCounts.marked}</span>
                </div>
                Marked
              </p>
              <p>
                <div className="status red">
                  <span>{sectionStatusCounts.notAnswered}</span>
                </div>
                Not Answered
              </p>
            </div>

            <div className="question-navigator">
              <div className="section-questions">
                <div className="section-header">
                  {examData.sections[currentSectionIndex]?.section_title}
                  <span className="question-count">
                    ({currentSectionQuestionsCount} questions)
                  </span>
                </div>
                <div className="questions-grid">
                  {currentSectionQuestions.map((question, localIndex) => {
                    const status = getQuestionStatus(
                      currentSectionIndex,
                      question.passageIndex,
                      question.questionIndex
                    );
                    const isActive =
                      currentSectionIndex === question.sectionIndex &&
                      currentPassageIndex === question.passageIndex &&
                      currentQuestionIndex === question.questionIndex;

                    return (
                      <button
                        key={question.id}
                        className={`question-btn ${status} ${
                          isActive ? "active" : ""
                        }`}
                        onClick={() =>
                          handleQuestionNavigation(
                            question.sectionIndex,
                            question.passageIndex,
                            question.questionIndex
                          )
                        }
                        disabled={isTimeUp}
                        title={`Question ${localIndex + 1}`}
                      >
                        {localIndex + 1}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="side-buttons">
              <button
                className="btn submit-btn"
                onClick={handleSubmitConfirmation}
                disabled={isSubmitting || isTimeUp}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Submitting...
                  </>
                ) : (
                  "Submit Test"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Submit Confirmation Modal */}
      {showSubmitModal && (
        <div className="modal-overlay">
          <div className="submit-modal">
            <div className="modal-header">
              <h5>Submit Test</h5>
            </div>
            <div className="modal-body">
              <div className="warning-icon">⚠️</div>
              <h6>Are you sure you want to submit the test?</h6>
              <p>
                You have answered {answeredQuestions} out of {totalQuestions}{" "}
                questions.
              </p>
              <div className="submission-warning">
                <strong>Once submitted, you cannot return to the test.</strong>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-cancel"
                onClick={() => setShowSubmitModal(false)}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                className="btn btn-submit"
                onClick={handleSubmitTest}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Yes, Submit Test"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamPage;
